import React from 'react';
import { Box, Typography, Button, Paper, alpha } from '@mui/material';
import {
  ConfirmationNumber as TicketIcon,
  FilterAlt as FilterIcon,
  Search as SearchIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useThemeSettings } from '../../../../contexts/ThemeContext';

/**
 * Context-aware empty state for tickets
 */
const TicketEmptyState = ({
  variant = 'no-data', // 'no-data', 'no-results', 'no-filter-results'
  hasCreatePermission = false,
  onCreateTicket,
  onClearFilters,
  onClearSearch,
  searchTerm = '',
  filterCount = 0
}) => {
  const { t } = useThemeSettings();

  const variants = {
    'no-data': {
      icon: TicketIcon,
      title: t ? t('Tickets.empty_title') : 'No tickets yet',
      description: t ? t('Tickets.empty_description') :
        'Get started by creating your first support ticket. Track issues, requests, and maintenance tasks all in one place.',
      actions: hasCreatePermission ? [
        {
          label: t ? t('Tickets.create_first_ticket') : 'Create First Ticket',
          onClick: onCreateTicket,
          variant: 'contained',
          icon: <AddIcon />
        }
      ] : [],
      tips: [
        t ? t('Tickets.tip_1') : 'Tickets help you track and resolve support issues efficiently',
        t ? t('Tickets.tip_2') : 'You can attach files, add messages, and link interventions',
        t ? t('Tickets.tip_3') : 'Set urgency levels to prioritize critical issues'
      ]
    },
    'no-results': {
      icon: SearchIcon,
      title: t ? t('Tickets.no_search_results') : `No results for "${searchTerm}"`,
      description: t ? t('Tickets.no_search_results_desc') :
        'Try adjusting your search terms or check for typos. You can use wildcards (*) for partial matches.',
      actions: [
        {
          label: t ? t('Common.clear_search') : 'Clear Search',
          onClick: onClearSearch,
          variant: 'outlined'
        },
        ...(hasCreatePermission ? [{
          label: t ? t('Tickets.create_ticket') : 'Create Ticket',
          onClick: onCreateTicket,
          variant: 'contained',
          icon: <AddIcon />
        }] : [])
      ],
      tips: [
        t ? t('Tickets.search_tip_wildcard') : 'Use * as a wildcard (e.g., "network*" matches "network error")',
        t ? t('Tickets.search_tip_case') : 'Search is case-insensitive',
        t ? t('Tickets.search_tip_fields') : 'Searches across ID, title, description, location, and device'
      ]
    },
    'no-filter-results': {
      icon: FilterIcon,
      title: t ? t('Tickets.no_filter_results') : 'No tickets match your filters',
      description: t ? t('Tickets.no_filter_results_desc') :
        `${filterCount} filter${filterCount !== 1 ? 's' : ''} applied. Try adjusting or removing some filters to see more results.`,
      actions: [
        {
          label: t ? t('Tickets.clear_filters') : 'Clear All Filters',
          onClick: onClearFilters,
          variant: 'outlined'
        },
        ...(hasCreatePermission ? [{
          label: t ? t('Tickets.create_ticket') : 'Create Ticket',
          onClick: onCreateTicket,
          variant: 'contained',
          icon: <AddIcon />
        }] : [])
      ],
      tips: null
    }
  };

  const config = variants[variant] || variants['no-data'];
  const IconComponent = config.icon;

  return (
    <Paper
      sx={{
        p: { xs: 3, md: 6 },
        textAlign: 'center',
        bgcolor: 'background.paper'
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          width: 80,
          height: 80,
          margin: '0 auto',
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1)
        }}
      >
        <IconComponent
          sx={{
            fontSize: 40,
            color: 'primary.main'
          }}
        />
      </Box>

      {/* Title */}
      <Typography
        variant="h5"
        gutterBottom
        sx={{ fontWeight: 500, mb: 1 }}
      >
        {config.title}
      </Typography>

      {/* Description */}
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}
      >
        {config.description}
      </Typography>

      {/* Actions */}
      {config.actions.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: config.tips ? 4 : 0, flexWrap: 'wrap' }}>
          {config.actions.map((action, index) => (
            <Button
              key={index}
              variant={action.variant}
              onClick={action.onClick}
              startIcon={action.icon}
              size="large"
            >
              {action.label}
            </Button>
          ))}
        </Box>
      )}

      {/* Tips */}
      {config.tips && (
        <Box
          sx={{
            mt: 4,
            pt: 3,
            borderTop: '1px solid',
            borderColor: 'divider',
            textAlign: 'left',
            maxWidth: 600,
            mx: 'auto'
          }}
        >
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{ fontWeight: 600, mb: 1.5 }}
          >
            {t ? t('Common.helpful_tips') : 'Helpful Tips'}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {config.tips.map((tip, index) => (
              <Typography
                key={index}
                component="li"
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                {tip}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default TicketEmptyState;
